import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import type { Sale, SaleItem } from '@/lib/types';
import {
  computeProfits,
  previousRange,
  profitRange,
  type ProfitPeriodMode,
  type ProfitProductMeta,
  type ProfitSummary,
  type ProductProfitRow,
} from '@/lib/profits';

function round2Safe(n: number): number {
  return Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;
}

export interface UseProfitsParams {
  mode: ProfitPeriodMode;
  customStart: string;
  customEnd: string;
  branchId: string | null;
  cashierId: string | null;
  productQuery: string;
  categoryId: string | null;
}

interface RangeData {
  sales: Sale[];
  items: SaleItem[];
  summary: ProfitSummary;
  rows: ProductProfitRow[];
}

function summarizeRows(rows: ProductProfitRow[], base: ProfitSummary): ProfitSummary {
  const revenue = rows.reduce((s, r) => s + r.revenue, 0);
  const cost = rows.reduce((s, r) => s + r.cost, 0);
  const profit = round2Safe(revenue - cost);
  return {
    ...base,
    quantity: rows.reduce((s, r) => s + r.quantity, 0),
    revenue: round2Safe(revenue),
    cost: round2Safe(cost),
    profit,
    margin: revenue > 0 ? round2Safe((profit / revenue) * 100) : 0,
  };
}

export function useProfits(params: UseProfitsParams) {
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState<RangeData | null>(null);
  const [prevSummary, setPrevSummary] = useState<ProfitSummary | null>(null);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { mode, customStart, customEnd, branchId, cashierId, productQuery, categoryId } = params;

  // Un cajero solo puede consultar sus propias ventas.
  const effectiveCashier = isAdmin ? cashierId : user?.id ?? null;

  const loadRange = useCallback(
    async (start: Date, end: Date): Promise<{ sales: Sale[]; items: SaleItem[] }> => {
      let salesQuery = supabase
        .from('sales')
        .select('id, subtotal, total, discount_code_amount, tax, refund_total, status, cashier_id, branch_id, created_at')
        .in('status', ['completed', 'partial_refund'])
        .gte('created_at', start.toISOString())
        .lt('created_at', end.toISOString());
      if (branchId) salesQuery = salesQuery.eq('branch_id', branchId);
      if (effectiveCashier) salesQuery = salesQuery.eq('cashier_id', effectiveCashier);

      const { data: salesData, error: salesErr } = await salesQuery;
      if (salesErr) throw salesErr;
      const sales = ((salesData as unknown) as Sale[]) ?? [];
      if (sales.length === 0) return { sales: [], items: [] };

      const saleIds = sales.map((s) => s.id);
      const { data: itemsData, error: itemsErr } = await supabase
        .from('sale_items')
        .select('id, sale_id, product_id, product_name, quantity, subtotal, unit_cost, returned_quantity')
        .in('sale_id', saleIds);
      if (itemsErr) throw itemsErr;

      return { sales, items: ((itemsData as unknown) as SaleItem[]) ?? [] };
    },
    [branchId, effectiveCashier]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const range = profitRange(mode, customStart, customEnd);
      const prev = previousRange(range);

      const [{ data: productsData }, { data: catData }] = await Promise.all([
        supabase.from('products').select('id, sku, category_id, purchase_price, sale_price'),
        supabase.from('categories').select('id, name').order('name'),
      ]);

      const products = ((productsData as unknown) as ProfitProductMeta[]) ?? [];
      const cats = (catData as { id: string; name: string }[]) ?? [];
      setCategories(cats);

      const [current, previous] = await Promise.all([loadRange(range.start, range.end), loadRange(prev.start, prev.end)]);

      const built = computeProfits(current.sales, current.items, products, cats);
      const builtPrev = computeProfits(previous.sales, previous.items, products, cats);

      setData({ sales: current.sales, items: current.items, summary: built.summary, rows: built.rows });
      setPrevSummary(builtPrev.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron calcular las ganancias');
      setData(null);
      setPrevSummary(null);
    } finally {
      setLoading(false);
    }
  }, [mode, customStart, customEnd, loadRange]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const q = productQuery.trim().toLowerCase();
    return data.rows
      .filter((r) => {
        if (categoryId && r.categoryId !== categoryId) return false;
        if (q) {
          const hay = `${r.name} ${r.sku ?? ''}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      });
  }, [data, productQuery, categoryId]);

  const summary = useMemo<ProfitSummary>(() => {
    if (!data) {
      return {
        salesCount: 0,
        quantity: 0,
        revenue: 0,
        cost: 0,
        profit: 0,
        margin: 0,
        tax: 0,
        refunds: 0,
      };
    }
    return summarizeRows(filteredRows, data.summary);
  }, [data, filteredRows]);

  const prevFilteredSummary = useMemo<ProfitSummary | null>(() => {
    if (!prevSummary) return null;
    // La comparación usa el mismo conjunto filtrado no disponible atrás; usamos el total del período previo.
    return prevSummary;
  }, [prevSummary]);

  return { rows: filteredRows, summary, prevSummary: prevFilteredSummary, categories, loading, error, reload: load };
}